import React from 'react';
import { Product } from '../../types';
import { useAuthStore } from '../../store/authStore';

// Podklad ikon s „barvou značky", když se barva nepoužije (prodejce, značka bez barvy).
// Ikony jsou bílý piktogram na průhledném pozadí, bez podkladu by na bílém zmizely.
// Stejná hodnota je v pdf.service (NEUTRALNI_PODKLAD_IKONY).
export const NEUTRALNI_PODKLAD_IKONY = '#585857';

interface ProductFlyerLayoutProps {
  product: Product;
  className?: string;
  customImageUrl?: string; // For preview before saving
  brandColor?: string | null; // barva značky; není-li, použije se černá (jako v PDF)
}

/**
 * Product layout component for flyer display
 * Matches the design from product form - black header, image left, description right
 */
export const ProductFlyerLayout: React.FC<ProductFlyerLayoutProps> = ({
  product,
  className = '',
  customImageUrl,
  brandColor
}) => {
  const { user } = useAuthStore();

  // Stejné pravidlo jako v pdf.service (renderProductSlot), aby náhled odpovídal PDF:
  // prodejci se barvy značek nezobrazují, jinak barva značky, jinak černá.
  const isEndUser = user?.role === 'end_user';
  const effectiveBrandColor = isEndUser ? null : (brandColor ?? product.brandColor ?? null);
  const headerColor = effectiveBrandColor || '#000000';
  // Pozor na React "falsy 0": product.originalPrice && ... by při 0 vykreslilo holou "0".
  // Porovnání s ?? 0 vrací vždy boolean.
  const maDoporucenouCenu = (product.originalPrice ?? 0) > product.price;
  const getProductImageUrl = (id: string) => {
    if (customImageUrl) return customImageUrl;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    const base = `${API_URL}/products/${id}/image`;
    // Bust the 1-year image cache when the product (and thus its image) changes.
    return product.updatedAt ? `${base}?v=${encodeURIComponent(product.updatedAt)}` : base;
  };

  return (
    <div className={`bg-white h-full flex flex-col overflow-hidden relative ${className}`}>
      {/* Watermark "VYPRODÁNO" přes vyprodaný produkt (soldOut) - obnoveno; shodné s PDF */}
      {product.soldOut && (
        <div className="absolute inset-0 z-10 pointer-events-none flex items-center justify-center">
          <div
            className="text-red-600 font-bold opacity-30 whitespace-nowrap"
            style={{
              fontSize: '1.8rem',
              transform: 'rotate(-45deg)',
              fontFamily: '"Vodafone Rg", "Arial Black", sans-serif',
              textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
              letterSpacing: '0.05em',
            }}
          >
            VYPRODÁNO
          </div>
        </div>
      )}

      {/* Hlavička v barvě značky (fallback černá) - shodné s PDF */}
      <div className="text-white px-2 py-1.5 flex-none flex items-center justify-center gap-2" style={{ backgroundColor: headerColor }}>
        {product.brandName && (
          <span className="font-bold text-[0.7rem] leading-tight" style={{ fontFamily: '"Vodafone Rg", "Arial Black", sans-serif' }}>
            {product.brandName}
          </span>
        )}
        <h4 className="text-[0.7rem] leading-tight line-clamp-2 text-center" style={{ fontFamily: '"Vodafone Rg", "Arial", sans-serif', fontWeight: 'normal' }}>
          {product.name}
        </h4>
      </div>

      {/* Main content area */}
      <div className="flex flex-1 min-h-0">
        {/* Left side: Image and prices (49%) */}
        <div className="flex flex-col" style={{ width: '49%' }}>
          {/* Product image with icons overlay */}
          <div className="flex-1 mb-1 min-h-0 relative" style={{ padding: '3px' }}>
            {/* Product image */}
            <img
              src={getProductImageUrl(product.id)}
              alt={product.name}
              className="w-full h-full object-contain"
              onError={(e) => {
                e.currentTarget.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23E5E7EB"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239CA3AF" font-size="12">No Img</text></svg>';
              }}
            />

            {/* Icons overlaid on left side - 4 fixed evenly distributed slots, icons fill from top */}
            {product.icons && product.icons.length > 0 && (
              <div
                className="absolute left-0 flex flex-col justify-between"
                style={{
                  top: '8px',
                  height: 'calc(100% - 16px)'
                }}
              >
                {/* Create 4 fixed slots, fill with icons from top */}
                {[0, 1, 2, 3].map((slotIndex) => {
                  const icon = product.icons[slotIndex];
                  return (
                    <div key={slotIndex} className="h-6 flex items-center">
                      {icon && (
                        <div
                          className={`h-6 flex items-center justify-center ${icon.isEnergyClass ? 'w-12' : 'w-6'}`}
                          style={icon.useBrandColor ? { backgroundColor: effectiveBrandColor || NEUTRALNI_PODKLAD_IKONY } : undefined}
                        >
                          <img
                            src={icon.imageUrl}
                            alt={icon.name}
                            className="h-6 w-full object-contain"
                            title={icon.name}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Prices section - no horizontal padding to align with product edge.
              Místo je vždy na DVA řádky cen, i když má produkt jen akční cenu -
              jinak by obrázek nad cenami (flex-1) u jedné ceny povyrostl a ikony
              by se rozjely jinak než u vedlejšího produktu. Stejně to dělá PDF
              (pevné pricesReservedHeight v pdf.service): jediná cena je hned pod
              obrázkem, pod ní zůstane volno. */}
          <div className="space-y-px flex-none pb-2">
            {maDoporucenouCenu && (
              <div className="flex gap-0.5">
                {/* Black box with white price - 50% width (ends at middle of image) */}
                <div className="bg-black text-white px-0.5 py-1.5 flex items-center justify-center" style={{ width: '50%' }}>
                  <div className="text-[0.75rem] leading-none" style={{ fontFamily: '"Vodafone Rg", "Arial Narrow", "Arial", sans-serif', fontWeight: 600, transform: 'scaleX(1.2)' }}>
                    {Math.round(product.originalPrice).toLocaleString('cs-CZ')} Kč
                  </div>
                </div>
                {/* Gray box with label - fills remaining space */}
                <div className="bg-gray-200 text-gray-700 py-1 flex items-center justify-start flex-1" style={{ paddingLeft: '6px' }}>
                  <div className="text-[0.5rem] leading-none" style={{ fontFamily: '"Vodafone Rg", "Arial", sans-serif', transform: 'scaleX(1.1)', transformOrigin: 'left' }}>
                    Doporučená<br/>cena
                  </div>
                </div>
              </div>
            )}
            {/* Red box with white price + gray label */}
            <div className="flex gap-0.5">
              {/* Red box with white price - 50% width (ends at middle of image) */}
              <div className="bg-red-600 text-white px-0.5 py-1.5 flex items-center justify-center" style={{ width: '50%' }}>
                <div className="text-[0.75rem] leading-none" style={{ fontFamily: '"Vodafone Rg", "Arial Narrow", "Arial", sans-serif', fontWeight: 600, transform: 'scaleX(1.2)' }}>
                  {Math.round(product.price).toLocaleString('cs-CZ')} Kč
                </div>
              </div>
              {/* Gray box with label - fills remaining space */}
              <div className="bg-gray-200 text-gray-700 py-1 flex items-center justify-start flex-1" style={{ paddingLeft: '6px' }}>
                <div className="text-[0.5rem] leading-none" style={{ fontFamily: '"Vodafone Rg", "Arial", sans-serif', transform: 'scaleX(1.1)', transformOrigin: 'left' }}>
                  Akční cena<br/>{maDoporucenouCenu ? 'Oresi' : ''}
                </div>
              </div>
            </div>
            {/* Neviditelná náhrada řádku doporučené ceny - drží stejnou výšku
                (stejná struktura a písmo jako skutečný řádek), viz komentář výše. */}
            {!maDoporucenouCenu && (
              <div className="flex gap-0.5 invisible" aria-hidden="true">
                <div className="px-0.5 py-1.5 flex items-center justify-center" style={{ width: '50%' }}>
                  <div className="text-[0.75rem] leading-none" style={{ fontFamily: '"Vodafone Rg", "Arial Narrow", "Arial", sans-serif', fontWeight: 600 }}>
                    0 Kč
                  </div>
                </div>
                <div className="py-1 flex items-center justify-start flex-1" style={{ paddingLeft: '6px' }}>
                  <div className="text-[0.5rem] leading-none" style={{ fontFamily: '"Vodafone Rg", "Arial", sans-serif' }}>
                    Doporučená<br/>cena
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right side: Description - full height (51%), max 15 visual lines */}
        {product.description && (
          <div className="bg-white overflow-hidden" style={{ width: '51%', padding: '1.5px', paddingLeft: '8px', paddingTop: '6px' }}>
            <div
              className="text-[0.55rem] overflow-hidden"
              style={{
                maxHeight: 'calc(0.55rem * 1.35 * 16)', // 16 visual lines: font-size * line-height * lines
                lineHeight: '1.35',
                fontFamily: '"Vodafone Rg", "Arial", sans-serif'
              }}
            >
              {product.description.split('\n').map((line, index) => (
                <div key={index} className="flex items-start gap-1">
                  <span className="flex-shrink-0">•</span>
                  <span className="flex-1 break-words">{line}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
